import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";

/**
 * What a screen looks like while it is still arriving.
 *
 * Four screens said "Loading..." in plain text, which tells you the page is not broken
 * and nothing else: not how much is coming, not where it will be, and it reflows the
 * whole layout the moment the data lands. `ui/skeleton.jsx` has been sitting in this
 * repo unused since the scaffold.
 *
 * The shapes deliberately match the real layout, so nothing jumps when it resolves.
 */

export function TableSkeleton({ rows = 8, cols = 6, testid = "table-skeleton" }) {
  return (
    <Card className="border border-border overflow-hidden" data-testid={testid}>
      <div className="bg-muted/70 border-b border-border px-4 py-3 flex gap-4">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className="h-3.5 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="px-4 py-3.5 flex gap-4 border-b border-border last:border-0">
          {Array.from({ length: cols }).map((_, c) => (
            // Varying widths, because a grid of identical bars reads as a loading
            // graphic rather than as the table that is about to appear.
            <Skeleton key={c} className="h-3.5 flex-1"
                      style={{ maxWidth: `${[100, 78, 92, 64, 86, 70][(r + c) % 6]}%` }} />
          ))}
        </div>
      ))}
    </Card>
  );
}

export function DetailSkeleton({ testid = "detail-skeleton" }) {
  return (
    <div className="space-y-5" data-testid={testid}>
      <Skeleton className="h-8 w-24" />
      <div className="space-y-2">
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-9 w-2/3 max-w-lg" />
        <Skeleton className="h-4 w-1/2 max-w-md" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="p-5 border border-border space-y-3">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-8 w-16" />
          </Card>
        ))}
      </div>
      <Card className="p-6 border border-border space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-4" style={{ width: `${[92, 70, 84, 60, 76][i]}%` }} />
        ))}
      </Card>
    </div>
  );
}
